import { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import Calendar from '@factorialco/f0-react/icons/app/Calendar'
import Phone from '@factorialco/f0-react/icons/app/Phone'
import { EnvironmentContext } from 'config/environment'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  GetFeatureAvailabilitiesDocument,
  GetUpsellDiscountsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import type { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'
import { trackingEvents } from 'modules/core/lib/Tracking/events'
import { track } from 'modules/core/lib/Tracking/tracker'
import i18n from 'modules/core/lib/i18n'

import { ResourceHeaderProps } from 'modules/marketplace_modules/components/Revamp/types'
import {
  allProjectFeatures,
  isProjectBusiness,
} from 'modules/marketplace_modules/components/utils/isProjectBusiness'
import { useMarketplaceUpsellRequest } from 'modules/marketplace_modules/hooks/useMarketplaceUpsellRequest'
import { isValidModule } from 'modules/marketplace_modules/lib/typesValidation'
import CheckoutModal from 'modules/upsell/components/SelfUpsell/CheckoutModal'
import { UpsellMeeting } from 'modules/upsell/components/UpsellMeeting'
import useBookMeeting from 'modules/upsell/hooks/useBookMeeting'
import { SelfUpsellProvider } from 'modules/upsell/store/SelfUpsellProvider'

const RevampResourceHeader = ({
  addonReference,
  crossSellingAddonReference,
  canSeeSelfUpsell,
  canSeeNormalUpsell,
  moduleData,
}: ResourceHeaderProps) => {
  const { ignoreUpsell } = useContext(EnvironmentContext)
  const { company } = useCurrentSession()
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false)
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false)
  const { moduleAvailabilities } = useModuleAvailabilities()
  const { pathname, search } = useLocation()
  const queryParams = new URLSearchParams(search)
  const actionParam = queryParams.get('action')
  const upsellOrigin = queryParams.get('upsell_origin')
  const currentSeats = company.subscription.currentCycleQuantity || company.subscription.seatCount
  const isLargeSegment = currentSeats >= 50

  const validChargebeeModuleName = isValidModule(moduleData?.chargebeeModuleName)
    ? moduleData?.chargebeeModuleName
    : undefined

  const {
    meetingLink,
    owner,
    loading: meetingLoading,
    error: meetingError,
  } = useBookMeeting({
    moduleRequested: validChargebeeModuleName || '',
  })

  const { executeUpsellRequest, UpsellDialog } = useMarketplaceUpsellRequest({
    moduleSlug: moduleData?.slug || undefined,
    moduleRequested: validChargebeeModuleName,
    requestSource: `From marketplace resource header ${moduleData?.slug}${
      upsellOrigin ? ` - ${upsellOrigin}` : ''
    }`,
    pageSource: pathname,
  })
  const isProjectsModule = moduleData?.slug === AvailableModules.projects

  const { data: featureData } = useQuery(GetFeatureAvailabilitiesDocument, {
    variables: {
      modules: [],
      ids: isProjectsModule ? [...allProjectFeatures, 'one_core'] : ['one_core'],
    },
    fetchPolicy: 'cache-first',
  })

  const { data: discountsData, loading: discountLoading } = useQuery(GetUpsellDiscountsDocument, {
    variables: {
      module: moduleData?.chargebeeModuleName,
    },
  })

  const discount = discountsData?.upsell?.upsellDiscountsConnection?.nodes?.[0]

  const featureAvailabilities = featureData?.companies.featureAvailabilities ?? []
  const isProjectBusinessResult = isProjectsModule
    ? isProjectBusiness(featureAvailabilities)
    : false

  const canSeeProjectsUpsell = isProjectBusinessResult

  const isAlreadyInstalled =
    moduleAvailabilities.find(
      (module: ModuleAvailabilityType) => module.id === moduleData?.chargebeeModuleName
    )?.available && !canSeeProjectsUpsell

  const isValidModuleSlug = (mod: string | null | undefined): mod is AvailableModules => {
    return typeof mod === 'string' && mod.length > 0
  }

  const daysUntilDiscountExpires = discount?.disabledAt
    ? Math.max(
        0,
        Math.ceil(
          (new Date(discount.disabledAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
        )
      )
    : 0

  const handleOpenCheckoutModal = () => {
    setIsCheckoutModalOpen(true)
    track(trackingEvents.marketplaceModules.openCheckoutModal, {
      source: 'marketplace_resource_header_checkout_modal',
      module: moduleData?.slug,
      origin: window.location.href,
    })
  }

  const handleCloseCheckoutModal = () => {
    setIsCheckoutModalOpen(false)
  }

  const handleOpenMeetingModal = () => {
    setIsMeetingModalOpen(true)
  }

  const handleCloseMeetingModal = () => {
    setIsMeetingModalOpen(false)
  }

  const getPrimaryAction = () => {
    const showSelfUpsellButton = canSeeSelfUpsell || ignoreUpsell

    if (showSelfUpsellButton) {
      return {
        label: i18n.t('marketplace.revamp.get_it_now'),
        onClick: handleOpenCheckoutModal,
      }
    }
    if (canSeeNormalUpsell) {
      return {
        label: i18n.t('upselling_kit.button.request_meeting'),
        onClick: executeUpsellRequest,
      }
    }

    return undefined
  }

  const getSecondaryActions = () => {
    const showSelfUpsellButton = canSeeSelfUpsell || ignoreUpsell
    const hasMeetingLink = !meetingLoading && !meetingError && meetingLink

    if (isLargeSegment && hasMeetingLink) {
      return [
        {
          label: i18n.t('upsell.book_a_meeting'),
          onClick: handleOpenMeetingModal,
          icon: Calendar,
        },
      ]
    }
    if (showSelfUpsellButton && isLargeSegment && !meetingLoading) {
      return [
        {
          label: i18n.t('upselling_kit.button.request_meeting'),
          onClick: executeUpsellRequest,
          icon: Phone,
        },
      ]
    }

    return undefined
  }

  useEffect(() => {
    if (!isAlreadyInstalled) {
      const hasMeetingLink = !meetingLoading && !meetingError && meetingLink
      const bookAMeetingButtonAppears = isLargeSegment && hasMeetingLink

      if (bookAMeetingButtonAppears) {
        track(trackingEvents.marketplaceModules.viewBookAMeetingButton, {
          module: moduleData?.slug,
          origin: window.location.href,
        })
      }
    }
  }, [
    isAlreadyInstalled,
    isLargeSegment,
    meetingLoading,
    meetingError,
    meetingLink,
    moduleData?.slug,
  ])

  useEffect(() => {
    if (!isAlreadyInstalled) {
      const showSelfUpsellButton = canSeeSelfUpsell || ignoreUpsell
      const hasMeetingLink = !meetingLoading && !meetingError && meetingLink
      const requestMeetingInPrimary = canSeeNormalUpsell
      const requestMeetingInSecondary =
        showSelfUpsellButton && isLargeSegment && !meetingLoading && !hasMeetingLink
      const requestMeetingButtonAppears = requestMeetingInPrimary || requestMeetingInSecondary

      if (requestMeetingButtonAppears) {
        track(trackingEvents.marketplaceModules.viewRequestAMeetingButton, {
          module: moduleData?.slug,
          origin: window.location.href,
          location: requestMeetingInPrimary ? 'primary' : 'secondary',
        })
      }
    }
  }, [
    isAlreadyInstalled,
    canSeeNormalUpsell,
    canSeeSelfUpsell,
    ignoreUpsell,
    isLargeSegment,
    meetingLoading,
    meetingError,
    meetingLink,
    moduleData?.slug,
  ])

  const shouldShowSelfUpsellProvider = canSeeSelfUpsell && addonReference?.length > 0

  useEffect(() => {
    if (actionParam === 'init-self-upsell' && shouldShowSelfUpsellProvider) {
      setIsCheckoutModalOpen(true)
    }
  }, [actionParam, shouldShowSelfUpsellProvider])

  const resourceHeaderProps = {
    avatar: {
      type: 'generic' as const,
      name: moduleData?.slug || '',
      src: moduleData?.image?.url || '',
    },
    title: moduleData?.name || '',
    description: moduleData?.shortDescription || '',
    primaryAction: isAlreadyInstalled ? undefined : getPrimaryAction(),
    secondaryActions: isAlreadyInstalled ? undefined : getSecondaryActions(),
    status: {
      label: 'Status',
      text: isAlreadyInstalled
        ? i18n.t('marketplace.revamp.in_your_plan')
        : i18n.t('marketplace.revamp.not_in_your_plan'),
      variant: isAlreadyInstalled ? ('positive' as const) : ('neutral' as const),
    },
    metadata: [
      ...(!discountLoading && discount && !isAlreadyInstalled
        ? [
            {
              label: '',
              value: {
                type: 'status' as const,
                label: i18n.t('marketplace.revamp.discount', {
                  percentage: Math.floor(discount.percentage),
                  period: discount.period,
                  periodUnit: discount.period > 1 ? discount.periodUnit + 's' : discount.periodUnit,
                }),
                variant: 'warning' as const,
              },
            },
            {
              label: i18n.t('marketplace.revamp.discount_ends_in'),
              value: {
                content: i18n.t('marketplace.revamp.reminder_discount_days', {
                  days: daysUntilDiscountExpires,
                }),
                type: 'text' as const,
              },
            },
          ]
        : []),

      {
        label: i18n.t('marketplace.revamp.category'),
        value: {
          content: moduleData?.category?.title || '',
          type: 'text' as const,
        },
      },
    ],
  }

  if (shouldShowSelfUpsellProvider) {
    return (
      <>
        <SelfUpsellProvider
          availableAddons={addonReference}
          availableCrossSellingAddons={crossSellingAddonReference}
          moduleName={moduleData?.chargebeeModuleName || ''}
        >
          <ResourceHeader {...resourceHeaderProps} />
          {isValidModuleSlug(moduleData?.chargebeeModuleName) && moduleData && (
            <CheckoutModal
              isOpen={isCheckoutModalOpen}
              onClose={handleCloseCheckoutModal}
              moduleName={moduleData.chargebeeModuleName}
              from={
                actionParam
                  ? `marketplace_resource_header_${actionParam}`
                  : 'marketplace_resource_header'
              }
            />
          )}
        </SelfUpsellProvider>
        <UpsellDialog />
        {meetingLink && (
          <UpsellMeeting
            isOpen={isMeetingModalOpen}
            onClose={handleCloseMeetingModal}
            meetingUrl={meetingLink}
            owner={owner}
            trackingSource="marketplace_resource_header_meeting_modal"
            moduleSlug={moduleData?.slug || undefined}
            moduleRequested={validChargebeeModuleName}
            requestSource={`From marketplace resource header ${moduleData?.slug}${
              upsellOrigin ? ` - ${upsellOrigin}` : ''
            }`}
            pageSource={pathname}
          />
        )}
      </>
    )
  }

  return (
    <>
      <ResourceHeader {...resourceHeaderProps} />
      <UpsellDialog />
      {meetingLink && (
        <UpsellMeeting
          isOpen={isMeetingModalOpen}
          onClose={handleCloseMeetingModal}
          meetingUrl={meetingLink}
          owner={owner}
          trackingSource="marketplace_resource_header_meeting_modal"
          moduleSlug={moduleData?.slug || undefined}
          moduleRequested={validChargebeeModuleName}
          requestSource={`From marketplace resource header ${moduleData?.slug}${
            upsellOrigin ? ` - ${upsellOrigin}` : ''
          }`}
          pageSource={pathname}
        />
      )}
    </>
  )
}

export default RevampResourceHeader
